import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

// ----------------------------------------------------------------------------

interface TableSkeletonProps {
  className?: string;
  rows?: number;
  columns?: number;
  mobileCards?: number;
}

export function TableSkeleton(props: TableSkeletonProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { className, rows = 6, columns = 6, mobileCards = 3 } = props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  // -------------------------------------
  // Effects
  // -------------------------------------

  // -------------------------------------
  // Component functions
  // -------------------------------------

  // -------------------------------------
  // Component local variables
  // -------------------------------------

  const rowIndexes = Array.from({ length: rows }, (_, index) => index);
  const columnIndexes = Array.from({ length: columns }, (_, index) => index);
  const mobileCardIndexes = Array.from(
    { length: mobileCards },
    (_, index) => index,
  );

  return (
    <div className={cn("w-full", className)}>
      <div className="space-y-3 lg:hidden">
        {mobileCardIndexes.map((index) => (
          <div
            key={`mobile-card-${index}`}
            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-3">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-5 w-40 max-w-full" />
                <Skeleton className="h-4 w-52 max-w-full" />
                <Skeleton className="h-4 w-28" />
              </div>
              <div className="space-y-2 shrink-0">
                <Skeleton className="h-3 w-12 ml-auto" />
                <Skeleton className="h-5 w-16" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block rounded-md border border-gray-100 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-gray-100">
              <TableHead className="w-12 text-center">
                <Skeleton className="mx-auto h-4 w-4 rounded-full" />
              </TableHead>
              {columnIndexes.map((index) => (
                <TableHead key={`header-${index}`}>
                  <Skeleton className="h-4 w-24" />
                </TableHead>
              ))}
              <TableHead className="w-12">
                <Skeleton className="h-4 w-4 ml-auto rounded-full" />
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rowIndexes.map((rowIndex) => (
              <TableRow
                key={`row-${rowIndex}`}
                className="border-gray-100 hover:bg-transparent"
              >
                <TableCell className="text-center">
                  <Skeleton className="mx-auto h-4 w-4 rounded-full" />
                </TableCell>
                {columnIndexes.map((columnIndex) => (
                  <TableCell key={`cell-${rowIndex}-${columnIndex}`}>
                    <Skeleton
                      className={cn(
                        "h-4",
                        columnIndex % 3 === 0 && "w-24",
                        columnIndex % 3 === 1 && "w-32",
                        columnIndex % 3 === 2 && "w-20",
                      )}
                    />
                  </TableCell>
                ))}
                <TableCell>
                  <Skeleton className="h-8 w-8 ml-auto rounded-md" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
